import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import AdminBar from '../components/AdminBar';
import { useThemeMode } from '../components/ThemeModeContext';
import { serverDomain } from './config';

const getFriendlyError = (message) => {
  if (!message) return 'Unable to load credit values right now.';
  if (message === 'Unauthorised - Access Denied') return message;
  if (message === 'Invalid or expired token!') return 'Your session has expired. Please log in again.';
  return message;
};

const parseCreditAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

export default function UpdateCredit() {
  const { isDarkMode } = useThemeMode();
  const headerBg = isDarkMode ? '#000000' : '#003366';
  const headerText = '#ffffff';
  const pageBg = isDarkMode ? '#1a1a1a' : '#ffffff';
  const pageText = isDarkMode ? '#f5f5f5' : '#000000';
  const cardBg = isDarkMode ? '#222222' : '#f7f7f7';
  const borderColor = isDarkMode ? '#3a3a3a' : '#d9d9d9';
  const inputBg = isDarkMode ? '#111111' : '#ffffff';

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [toast, setToast] = useState('');
  const [dialog, setDialog] = useState({ open: false, user: null, value: '', loading: false });
  const [hoveredUserId, setHoveredUserId] = useState(null);
  const timeoutRef = useRef(null);
  const inputRef = useRef(null);

  const isAdmin = localStorage.getItem('appstar_admin') === 'true';
  const token = localStorage.getItem('appstar_jwt');

  const showToast = (message) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setToast(message);
    timeoutRef.current = setTimeout(() => setToast(''), 3000);
  };

  const loadCredits = React.useCallback(() => {
    if (!token || !isAdmin) {
      setUsers([]);
      setErrorMessage('Unauthorised - Access Denied');
      setLoading(false);
      return Promise.resolve();
    }

    setLoading(true);
    setErrorMessage('');

    return axios
      .get(serverDomain + 'get-credit-values.php', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      .then((response) => {
        const data = response?.data || {};
        if (data.success === 1) {
          setUsers(Array.isArray(data.users) ? data.users : []);
          setErrorMessage('');
          return;
        }

        setUsers([]);
        setErrorMessage(getFriendlyError(data.message));
      })
      .catch((error) => {
        const data = error?.response?.data || {};
        setUsers([]);
        setErrorMessage(getFriendlyError(data.message || error.message));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isAdmin, token]);

  useEffect(() => {
    loadCredits();
  }, [loadCredits]);

  useEffect(() => {
    if (!dialog.open) return undefined;
    const timerId = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 0);

    return () => clearTimeout(timerId);
  }, [dialog.open]);

  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const closeDialog = () => {
    if (dialog.loading) return;
    setDialog({ open: false, user: null, value: '', loading: false });
  };

  const openDialog = (user) => {
    setDialog({ open: true, user, value: '', loading: false });
  };

  const handleUpdateCredit = () => {
    const creditValue = parseCreditAmount(dialog.value);

    if (!token || !isAdmin || !dialog.user || creditValue === null) {
      showToast('Credit could not be updated');
      return;
    }

    setDialog((current) => ({ ...current, loading: true }));

    axios
      .post(serverDomain + 'update-credit.php', null, {
        params: {
          jwt: token,
          userId: dialog.user.user_id,
          credit: creditValue
        }
      })
      .then((response) => {
        const data = response?.data || {};
        if (data.success === 1) {
          showToast('Credit updated');
          setDialog({ open: false, user: null, value: '', loading: false });
          return loadCredits();
        }

        showToast('Credit could not be updated');
        setDialog((current) => ({ ...current, loading: false }));
        return null;
      })
      .catch(() => {
        showToast('Credit could not be updated');
        setDialog((current) => ({ ...current, loading: false }));
      });
  };

  return (
    <div style={{ minHeight: '100vh', background: pageBg, color: pageText }}>
      <AdminBar bgColor={headerBg} textColor={headerText} />
      <div style={{ padding: '60px 24px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <h1 style={{ margin: 0, fontSize: '1.8rem' }}>Update Credit</h1>
        <p style={{ marginTop: 10, opacity: 0.82, maxWidth: 720 }}>
          Review all users and add credit to an account when needed.
        </p>

        {loading && <p style={{ marginTop: 20 }}>Loading credit values...</p>}
        {!loading && errorMessage && <p style={{ marginTop: 20 }}>{errorMessage}</p>}

        {!loading && !errorMessage && (
          <div style={{ marginTop: 20, borderRadius: 16, background: cardBg, border: `1px solid ${borderColor}`, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                <thead>
                  <tr style={{ background: isDarkMode ? '#181818' : '#eef4fb' }}>
                    <th style={{ textAlign: 'left', padding: '14px 16px', borderBottom: `1px solid ${borderColor}` }}>Full Name</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', borderBottom: `1px solid ${borderColor}` }}>Username</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', borderBottom: `1px solid ${borderColor}` }}>Current Credit</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', borderBottom: `1px solid ${borderColor}` }}>Recharge</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.user_id}>
                      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${borderColor}` }}>{user.full_name}</td>
                      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${borderColor}` }}>{user.username}</td>
                      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${borderColor}` }}>{user.credit}</td>
                      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${borderColor}` }}>
                        <button
                          type="button"
                          onClick={() => openDialog(user)}
                          onMouseEnter={() => setHoveredUserId(user.user_id)}
                          onMouseLeave={() => setHoveredUserId(null)}
                          style={{
                            padding: '10px 16px',
                            borderRadius: 10,
                            border: 'none',
                            cursor: 'pointer',
                            background: hoveredUserId === user.user_id ? (isDarkMode ? '#0f0f0f' : '#0a4577') : headerBg,
                            color: '#ffffff',
                            fontWeight: 600,
                            boxShadow: hoveredUserId === user.user_id ? '0 6px 18px rgba(0,0,0,0.18)' : 'none',
                            transform: hoveredUserId === user.user_id ? 'translateY(-1px)' : 'translateY(0)',
                            transition: 'background-color 140ms ease, box-shadow 140ms ease, transform 140ms ease'
                          }}
                        >
                          Update
                        </button>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan="4" style={{ padding: '24px 16px', textAlign: 'center' }}>
                        No users found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {dialog.open && (
        <div
          role="presentation"
          onClick={closeDialog}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            zIndex: 9500
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="update-credit-title"
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 420,
              borderRadius: 16,
              background: cardBg,
              color: pageText,
              padding: 24,
              boxShadow: '0 14px 32px rgba(0,0,0,0.24)'
            }}
          >
            <h2 id="update-credit-title" style={{ margin: 0, fontSize: '1.35rem' }}>Update Credit</h2>
            <p style={{ marginTop: 10, marginBottom: 14, opacity: 0.82 }}>
              Add credit for {dialog.user ? dialog.user.full_name : 'this user'}.
            </p>
            <input
              ref={inputRef}
              type="number"
              step="any"
              value={dialog.value}
              onChange={(event) => setDialog((current) => ({ ...current, value: event.target.value }))}
              placeholder="Enter credit amount"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '12px 14px',
                borderRadius: 10,
                border: `1px solid ${borderColor}`,
                background: inputBg,
                color: pageText,
                fontSize: '1rem'
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleUpdateCredit();
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  closeDialog();
                }
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 18 }}>
              <button
                type="button"
                onClick={closeDialog}
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: `1px solid ${borderColor}`,
                  background: 'transparent',
                  color: pageText,
                  cursor: dialog.loading ? 'default' : 'pointer'
                }}
                disabled={dialog.loading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpdateCredit}
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: headerBg,
                  color: '#ffffff',
                  cursor: dialog.loading ? 'default' : 'pointer',
                  fontWeight: 600,
                  opacity: dialog.loading ? 0.8 : 1
                }}
                disabled={dialog.loading}
              >
                {dialog.loading ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)', background: toast === 'Credit updated' ? '#176b32' : '#b42318', color: '#ffffff', padding: '10px 16px', borderRadius: 10, zIndex: 9800, boxShadow: '0 6px 18px rgba(0,0,0,0.2)' }}>
          {toast}
        </div>
      )}
    </div>
  );
}